import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { CardsVerticalScrollItemData } from './types';

interface UseAnchorNavigationOptions {
  cards: CardsVerticalScrollItemData[];
  scrollToIndex: (index: number) => void;
}

/**
 * Hook to handle anchor-based navigation for CardsVerticalScroll.
 * Detects URL hash on mount and hashchange events, then scrolls to the matching card.
 *
 * IMPORTANT: This hook must override the browser's native anchor scroll behavior.
 * When the browser sees a hash in the URL, it automatically scrolls to the element
 * with that ID. We need to scroll multiple times to ensure our scroll takes precedence.
 */
export const useAnchorNavigation = ({ cards, scrollToIndex }: UseAnchorNavigationOptions): void => {
  // Track the last hash we successfully navigated to, preventing re-triggers
  const lastHandledHashRef = useRef<string | null>(null);
  // Store the latest callbacks in refs to avoid effect dependency issues
  const scrollToIndexRef = useRef(scrollToIndex);
  const cardsRef = useRef(cards);

  // Keep refs up to date
  useLayoutEffect(() => {
    scrollToIndexRef.current = scrollToIndex;
    cardsRef.current = cards;
  }, [scrollToIndex, cards]);

  const cardsKey = cards.map(card => `${card.sys.id}:${card.sectionName ?? ''}`).join('|');

  /**
   * Find the index of a card by its sectionName matching the hash
   */
  const findCardIndexByHash = useCallback((hash: string): number => {
    if (!hash) return -1;

    // Remove the leading '#' from the hash
    const sectionName = hash.slice(1);
    if (!sectionName) return -1;

    return cardsRef.current.findIndex(card => card.sectionName === sectionName);
  }, []);

  const scrollToCardWithOverride = useCallback((cardIndex: number) => {
    scrollToIndexRef.current(cardIndex);
  }, []);

  /**
   * Handle navigation to the card matching the given hash.
   * Only navigates if this is a new hash we haven't handled yet.
   */
  const navigateToHash = useCallback(
    (hash: string) => {
      // Skip if we've already handled this exact hash
      if (hash === lastHandledHashRef.current) {
        return;
      }

      const cardIndex = findCardIndexByHash(hash);

      if (cardIndex >= 0) {
        // Mark this hash as handled before scrolling
        lastHandledHashRef.current = hash;
        scrollToCardWithOverride(cardIndex);
      }
    },
    [findCardIndexByHash, scrollToCardWithOverride],
  );

  // Handle initial hash on mount - use useLayoutEffect to run before browser paint
  // This gives us the best chance to scroll before the browser's native anchor scroll
  useLayoutEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    // Wait for cards to hydrate; otherwise we'd "attempt" too early and never retry.
    if (cardsRef.current.length === 0) return;

    navigateToHash(hash);
  }, [cardsKey, navigateToHash]);

  // Listen for hashchange events for in-page anchor clicks
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      // Reset the last handled hash so we can navigate again
      // (user explicitly clicked a link, so they want to go there)
      if (hash !== lastHandledHashRef.current) {
        navigateToHash(hash);
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [navigateToHash]);

  // Intercept clicks on anchor links to prevent browser's default scroll
  // and handle the navigation ourselves
  useEffect(() => {
    const handleAnchorClick = (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href^="#"]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const hash = anchor.getAttribute('href');
      if (!hash || !hash.startsWith('#')) return;

      const cardIndex = findCardIndexByHash(hash);

      // Only intercept if this hash matches one of our cards
      if (cardIndex >= 0) {
        event.preventDefault();
        /**
         * Runtime evidence shows `history.pushState(..., '#hash')` can still trigger a native
         * anchor scroll on mobile (even though we preventDefault on the click), which fights
         * our scroll model (cards are absolutely positioned and re-positioned).
         *
         * Fix: perform our scroll first, then update the URL hash AFTER, and immediately
         * restore scroll position on the next frame.
         */

        // Reset last handled hash to allow re-navigation to same card
        lastHandledHashRef.current = null;

        // Navigate to the card (our scroll model)
        navigateToHash(hash);

        // After our scroll, update the URL hash, then immediately restore scroll position
        // to prevent the browser's native anchor jump from winning.
        requestAnimationFrame(() => {
          const beforeHashUpdateScrollTop = window.pageYOffset || document.documentElement.scrollTop;

          window.history.pushState(null, '', hash);

          requestAnimationFrame(() => {
            // Restore the scroll position after any native anchor scroll has fired
            window.scrollTo({ top: beforeHashUpdateScrollTop, behavior: 'instant' });
          });
        });
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, [findCardIndexByHash, navigateToHash]);
};
